import React from 'react';
import { isUndefined } from 'lodash-es';

import { buildTrafficPlans, getTrafficOptionRegion, getTrafficPlanId, mapTrafficHighlights } from '../utils';
import {
  ConfiguratorContextTrafficPlan,
  ConfiguratorEngagementVideo,
  ConfiguratorTrafficHighlight,
  ConfiguratorTrafficPlan,
  ConfiguratorTrafficPlanClickHandler,
  TrafficOption,
} from '../types';

export function useTrafficStep(
  trafficOptions: TrafficOption[] = [],
  countryCode: string | undefined,
  locale?: string,
) {
  const [plan, setPlan] = React.useState<ConfiguratorTrafficPlan | undefined>();

  // Options offered in the selected region, with the region's name. Re-derives when the
  // country changes so a different country yields a different set of plans.
  const plans = React.useMemo<ConfiguratorTrafficPlan[]>(
    () => buildTrafficPlans(trafficOptions, countryCode, locale),
    [trafficOptions, countryCode, locale],
  );

  // Keep the selection valid: default to the first plan, and reset when the current plan
  // is no longer offered (e.g. after a country change).
  React.useEffect(() => {
    setPlan((current) => {
      if (plans.length === 0) {
        return undefined;
      }
      const stillValid = current && plans.some((p) => p.id === current.id);
      return stillValid ? current : plans[0];
    });
  }, [plans]);

  const handlePlanClick = React.useCallback<ConfiguratorTrafficPlanClickHandler>((nextPlan) => {
    setPlan(nextPlan);
  }, []);

  const trafficPlans = React.useMemo<ConfiguratorContextTrafficPlan[]>(() => {
    return plans.map((trafficPlan) => ({
      ...trafficPlan,
      active: trafficPlan.id === plan?.id,
      onClick: () => handlePlanClick(trafficPlan),
    }));
  }, [plans, plan, handlePlanClick]);

  // Highlights for the selected plan, resolved against the selected region.
  const trafficHighlights = React.useMemo<ConfiguratorTrafficHighlight[]>(() => {
    if (isUndefined(plan) || isUndefined(countryCode)) {
      return [];
    }
    const option = trafficOptions.find((o) => getTrafficPlanId(o) === plan.id);
    const region = getTrafficOptionRegion(option, countryCode);
    return mapTrafficHighlights(region?.highlights, locale);
  }, [plan, countryCode, trafficOptions, locale]);

  // The selected plan's center-stage promo video, shaped like engagementVideos so the
  // shared `[oh] preview` g-videos list (links src/loop, blends on isBlending) can render
  // it on the traffic step. The endpoint exposes it at `regions[code].center_stage`
  // (older payloads used `regions[code].video`).
  const trafficVideos = React.useMemo<ConfiguratorEngagementVideo[]>(() => {
    if (isUndefined(plan) || isUndefined(countryCode)) {
      return [];
    }
    const option = trafficOptions.find((o) => getTrafficPlanId(o) === plan.id);
    const region = getTrafficOptionRegion(option, countryCode) as any;
    const centerStage = region?.center_stage;
    const src = centerStage?.src || region?.video;
    if (!src) {
      return [];
    }
    return [
      {
        key: 'traffic-center-stage',
        itemId: 0,
        tierId: 0,
        src,
        loop: centerStage?.loop ?? true,
        isBlending: centerStage?.isBlending ?? false,
      } as any,
    ];
  }, [plan, countryCode, trafficOptions]);

  return React.useMemo(
    () => ({
      trafficPlan: plan,
      trafficPlans,
      trafficHighlights,
      trafficVideos,
    }),
    [plan, trafficPlans, trafficHighlights, trafficVideos],
  );
}
